import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { getUser, getUsers } from '@/services/users.service';
import { type TenantUserRole } from '@/types/tenantUserRole';
import { QueryKey } from '@/lib/queryKeys';

export const useUserList = (
  params: {
    pagination: { limit: number; offset: number };
    sort: { order: string; value: string };
    queryString: string;
    roles?: Array<TenantUserRole>;
  },
  options?: object,
) => {
  const userListQueryFn = useAuthenticatedQueryFn(getUsers);

  const {
    data: userList = {
      users: [],
      pagination: { count: 0, limit: 0, offset: 0 },
      sort: { order: SortOrderOptions.ASC, value: SortOrderValues.CREATED_AT },
    },
    isPending: userListIsPending,
    isFetching: userListIsFetching,
    isFetched: userListIsFetched,
    error: userListError,
    refetch: refetchUserList,
  } = useQuery({
    queryKey: [QueryKey.UserList, params.pagination, params.queryString, params.sort, params.roles],
    queryFn: () => userListQueryFn(params),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    userList,
    userListIsFetching,
    userListIsPending,
    userListIsFetched,
    refetchUserList,
    userListError,
  };
};

export const useUser = (id: string, options?: object) => {
  const userQueryFn = useAuthenticatedQueryFn(getUser);

  const {
    data: user = {},
    isPending: userIsPending,
    isFetching: userIsFetching,
    error: userError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: [QueryKey.User, id],
    queryFn: () => userQueryFn({ id }),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    user,
    userIsFetching,
    userIsPending,
    refetchUser,
    userError,
  };
};
